import type { ReactNode } from 'react';
import { KbdGroup, Kbd as KbdKey } from '@/components/ui/kbd';
import { cn } from '@/lib/utils';

/** True on macOS and iOS, where the modifier is ⌘ (§5.3: "Ctrl K" or "⌘K"). */
function isMac(): boolean {
  try {
    const nav = navigator as Navigator & {
      userAgentData?: { platform?: string };
    };
    const platform =
      nav.userAgentData?.platform ?? nav.platform ?? nav.userAgent;
    return /mac|iphone|ipad|ipod/i.test(platform);
  } catch {
    return false;
  }
}

interface KeyName {
  /** Shown on a Mac. */
  mac: string;
  /** Shown elsewhere. */
  other: string;
  /** Read by screen readers. */
  spoken: { mac: string; other: string };
}

function key(mac: string, other: string, spokenMac = mac, spokenOther = other) {
  return { mac, other, spoken: { mac: spokenMac, other: spokenOther } };
}

const KEY_NAMES: Record<string, KeyName> = {
  mod: key('⌘', 'Ctrl', 'Command', 'Control'),
  ctrl: key('⌃', 'Ctrl', 'Control', 'Control'),
  alt: key('⌥', 'Alt', 'Option', 'Alt'),
  shift: key('⇧', 'Shift', 'Shift', 'Shift'),
  enter: key('↵', 'Enter', 'Return', 'Enter'),
  escape: key('Esc', 'Esc', 'Escape', 'Escape'),
  esc: key('Esc', 'Esc', 'Escape', 'Escape'),
  arrowleft: key('←', '←', 'Left arrow', 'Left arrow'),
  arrowright: key('→', '→', 'Right arrow', 'Right arrow'),
  arrowup: key('↑', '↑', 'Up arrow', 'Up arrow'),
  arrowdown: key('↓', '↓', 'Down arrow', 'Down arrow'),
  space: key('Space', 'Space'),
  tab: key('Tab', 'Tab'),
};

function splitKeys(keys: string | readonly string[]): string[] {
  if (typeof keys !== 'string') return [...keys];
  // "mod+k", "shift+d", "alt+arrowleft"; a lone "+" stays a key.
  return keys === '+' ? ['+'] : keys.split('+').filter(Boolean);
}

function visible(part: string, mac: boolean): string {
  const name = KEY_NAMES[part.toLowerCase()];
  if (name) return mac ? name.mac : name.other;
  return part.length === 1 ? part.toUpperCase() : part;
}

function spoken(part: string, mac: boolean): string {
  const name = KEY_NAMES[part.toLowerCase()];
  if (name) return mac ? name.spoken.mac : name.spoken.other;
  return part.length === 1 ? part.toUpperCase() : part;
}

export interface KbdProps {
  /**
   * The keys of one shortcut: "mod+k", "shift+d", "alt+arrowleft", "?", or a
   * list. `mod` is ⌘ on a Mac and Ctrl elsewhere.
   */
  keys?: string | readonly string[];
  /** Free text instead of `keys` ("g then h"). */
  children?: ReactNode;
  className?: string;
}

/**
 * A keyboard hint (§5.3, §6.11): "Ctrl K" as separate keys, or "⌘K" as one on
 * a Mac. Screen readers hear the key names ("Control K").
 */
export function Kbd({ keys, children, className }: Readonly<KbdProps>) {
  if (children !== undefined || keys === undefined) {
    return <KbdKey className={className}>{children}</KbdKey>;
  }
  const mac = isMac();
  const parts = splitKeys(keys);
  const said = parts.map((p) => spoken(p, mac)).join(' ');

  if (mac) {
    return (
      <KbdKey className={className}>
        <span aria-hidden="true">
          {parts.map((p) => visible(p, true)).join('')}
        </span>
        <span className="sr-only">{said}</span>
      </KbdKey>
    );
  }
  return (
    <KbdGroup className={cn(className)}>
      <span aria-hidden="true" className="inline-flex items-center gap-1">
        {parts.map((p) => (
          <KbdKey key={p}>{visible(p, false)}</KbdKey>
        ))}
      </span>
      <span className="sr-only">{said}</span>
    </KbdGroup>
  );
}
